// Copyright ©️ 2026 eVolve MEP, LLC

import { useRef, useEffect, useCallback, type ReactNode } from 'react';

import type { EvolveApiRoute, EvolveApiResponse } from '@evolvemep/api-ts';
import { useUncontrolled } from '@mantine/hooks';

import type { WrappedCheckListProps } from 'components/Mantine/TypeSafeSelect/types';
import { WrappedCheckList } from 'components/Mantine/TypeSafeSelect/WrappedCheckList';
import { type KeysOfType, hasLengthExactly } from 'helpers/helper-types';
import { isNotNil } from 'helpers/isNotNil';

import type { SearchableMultiSelectorProps } from './types';
import { useSearchableData } from './useSearchableData';

type Props<
  Route extends EvolveApiRoute<'GET_PAGINATED'>,
  T extends EvolveApiResponse<'GET_PAGINATED', Route>['data'][number],
  TKey extends KeysOfType<T, string>,
  TLabel extends ReactNode = string,
> = SearchableMultiSelectorProps<Route, T, TKey, TLabel> &
  Omit<WrappedCheckListProps<string, object>, keyof SearchableMultiSelectorProps<Route, T, TKey, TLabel> | 'data'>;

/**
 * Used for Check Lists whose "search" functionality
 * involves fetching data from the backend.
 */
export const SearchableCheckList = <
  Route extends EvolveApiRoute<'GET_PAGINATED'>,
  T extends EvolveApiResponse<'GET_PAGINATED', Route>['data'][number],
  TKey extends KeysOfType<T, string>,
  TLabel extends ReactNode = string,
>({
  paginatedGet,
  getItemLabel,
  idKey,
  value: parentValue,
  onChange: parentOnChange,
  excludeOption,
  searchKey,

  useLike,
  defaultValue: parentDefaultValue,
  onOptionSubmit,
  disableOption,
  autoSelectWhenOnlyOneOption,
  ...props
}: Props<Route, T, TKey, TLabel>) => {
  const isFirstPull = useRef(true);
  const { entireCount, options, fetchNextPage, loading, pendingSearchPhrase, searchPhrase, setSearchPhrase } =
    useSearchableData<Route, T, TKey, string>({
      paginatedGet,
      searchKey,
      useLike,
      excludeOption,
    });

  const [selectedOptions, setSelectedOptions] = useUncontrolled<T[]>({
    value: parentValue,
    defaultValue: parentDefaultValue,
    onChange: parentOnChange,
  });

  useEffect(() => {
    if (isFirstPull.current && options.length > 0) {
      isFirstPull.current = false;
      if (hasLengthExactly(options, 1) && autoSelectWhenOnlyOneOption) {
        setSelectedOptions(options);
      }
    }
  }, [autoSelectWhenOnlyOneOption, options, setSelectedOptions]);

  // Override the WrappedCheckList's default filtering
  // since we're handling it server side
  const filterOverride = useCallback(() => true, []);

  return (
    <WrappedCheckList
      {...props}
      disableOption={
        isNotNil(disableOption)
          ? (v) => {
              const opt = options.find((o) => o[idKey] === v);
              return isNotNil(opt) ? disableOption(opt) : false;
            }
          : undefined
      }
      onChange={(v) => {
        const ids = v as T[TKey][];
        const stillSelected = selectedOptions.filter((t) => ids.includes(t[idKey]));
        const additionalSelected = options.filter(
          (o) => ids.includes(o[idKey]) && !stillSelected.some((t) => t[idKey] === o[idKey]),
        );
        const newSelectedOptions = [...stillSelected, ...additionalSelected];
        setSelectedOptions(newSelectedOptions);
      }}
      onOptionSubmit={
        isNotNil(onOptionSubmit)
          ? (id) => {
              const opt = selectedOptions.find((o) => o[idKey] === id) ?? options.find((o) => o[idKey] === id);
              if (isNotNil(opt)) {
                onOptionSubmit(opt);
              }
            }
          : undefined
      }
      value={selectedOptions.map((t) => t[idKey] as string)}
      data={[...selectedOptions.filter((t) => !options.some((o) => o[idKey] === t[idKey])), ...options].map((o) => ({
        value: o[idKey] as string,
        label: getItemLabel(o),
      }))}
      loading={loading}
      onScrollToEnd={fetchNextPage}
      entireCount={entireCount}
      filterInclude={filterOverride}
      onSearchChange={setSearchPhrase}
      searchValue={searchPhrase}
      pendingSearchValue={pendingSearchPhrase}
    />
  );
};
